import { defaultTo, comparator, lt, isNil, isEmpty } from 'ramda';

import type { Prospect } from '../../containers/ProspectProfile/types';
import {
  DEFAULT_EMAIL_ENTRY,
  DEFAULT_PHONE_ENTRY,
} from './ContactFields/constants';

// Helper function to sort contacts by isPrimary (primary first)
const sortContactsByPrimary = (contacts) => {
  return contacts.sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    return 0;
  });
};

// Helper function to check if contact info is missing (null, undefined, or empty array)
const isContactInfoMissing = (contactInfo) =>
  isNil(contactInfo) || isEmpty(contactInfo);

export function getAffordableRelationshipDefault(
  prospect: Object,
  hohRelationshipId: string,
): string {
  const { id, primaryApplicantId } = prospect;
  return id === primaryApplicantId ? hohRelationshipId ?? 'default' : 'default';
}

export function getMainEmailAndPhone(
  emails,
  phones,
  { emailAddress, phoneNumber },
) {
  const email = emails?.length
    ? emails.find((e) => e.isPrimary)?.emailAddress
    : emailAddress;
  const phone = phones?.length
    ? phones.find((p) => p.isPrimary)?.phoneNumber
    : phoneNumber;
  return { email, phone };
}

export function generateInitialAdult(
  prospect?: Prospect,
  headOfHouseholdRelationship?: Object = {},
) {
  const { value: hohRelationshipId } = headOfHouseholdRelationship;
  const defaultToDefault = defaultTo('default');
  const byCreationDate = comparator((a, b) => lt(a.createdAt, b.createdAt));
  const defaultPhoneType = DEFAULT_PHONE_ENTRY.phoneType;
  const defaultEmailType = DEFAULT_EMAIL_ENTRY.emailType;

  if (prospect) {
    const phones: ContactPhone[] = isContactInfoMissing(
      prospect.contact?.phones,
    )
      ? isNil(prospect.phoneNumber)
        ? []
        : [
            {
              phoneNumber: prospect.phoneNumber,
              phoneType: prospect.phoneType || defaultPhoneType,
              isPrimary: true,
            },
          ]
      : sortContactsByPrimary([...prospect.contact.phones]).map((phone) => ({
          ...phone,
          phoneType: (phone.phoneType || defaultPhoneType).toLowerCase(),
        }));
    const emails: ContactEmail[] = isContactInfoMissing(
      prospect.contact?.emails,
    )
      ? isNil(prospect.emailAddress)
        ? []
        : [
            {
              emailAddress: prospect.emailAddress,
              emailType: prospect.emailType || defaultEmailType,
              isPrimary: true,
            },
          ]
      : sortContactsByPrimary([...prospect.contact.emails]).map((email) => ({
          ...email,
          emailType: (email.emailType || defaultEmailType).toLowerCase(),
        }));

    const { email, phone } = getMainEmailAndPhone(emails, phones, prospect);

    const prospectApplicant = {
      id: prospect.id,
      firstName: prospect.firstName,
      middleName: prospect.middleName,
      lastName: prospect.lastName,
      preferredName: prospect.preferredName,
      phone,
      email,
      phones,
      emails,
      type: 'default',
      relationship: 'default',
      affordableRelationship: getAffordableRelationshipDefault(
        prospect,
        hohRelationshipId,
      ),
      suffixId: defaultToDefault(prospect.suffixId),
      isProspect: true,
    };

    const adults = prospect.additionalOccupants
      ? [
          prospectApplicant,
          ...prospect.additionalOccupants
            .filter((occupant) => occupant.type !== 'Minor')
            .sort(byCreationDate)
            .map((occupant) => ({
              id: occupant.id,
              firstName: occupant.firstName,
              middleName: occupant.middleName,
              lastName: occupant.lastName,
              preferredName: occupant.preferredName,
              phone: occupant.phoneNumber,
              email: occupant.emailAddress,
              phones: isContactInfoMissing(occupant.contact?.phones)
                ? isNil(occupant.phoneNumber)
                  ? []
                  : [
                      {
                        phoneNumber: occupant.phoneNumber,
                        phoneType: (
                          occupant.phoneType || defaultPhoneType
                        ).toLowerCase(),
                        isPrimary: true,
                      },
                    ]
                : sortContactsByPrimary([...occupant.contact.phones]).map(
                    (phone) => ({
                      ...phone,
                      phoneType: (
                        phone.phoneType || defaultPhoneType
                      ).toLowerCase(),
                    }),
                  ),
              emails: isContactInfoMissing(occupant.contact?.emails)
                ? isNil(occupant.emailAddress)
                  ? []
                  : [
                      {
                        emailAddress: occupant.emailAddress,
                        emailType: occupant.emailType || defaultEmailType,
                        isPrimary: true,
                      },
                    ]
                : sortContactsByPrimary([...occupant.contact.emails]).map(
                    (email) => ({
                      ...email,
                      emailType: email.emailType || defaultEmailType,
                    }),
                  ),
              type: 'default',
              relationship: defaultToDefault(occupant.relationshipId),
              affordableRelationship: defaultToDefault(
                occupant.affordableRelationshipId,
              ),
              suffixId: defaultToDefault(occupant.suffixId),
              isProspect: false,
            })),
        ]
      : [];
    return adults;
  }
  return [];
}

export function generateInitialMinor(prospect?: Prospect) {
  const defaultToDefault = defaultTo('default');
  const byCreationDate = comparator((a, b) => lt(a.createdAt, b.createdAt));
  if (prospect) {
    return prospect.additionalOccupants
      ? prospect.additionalOccupants
          .filter((ao) => ao.type === 'Minor')
          .sort(byCreationDate)
          .map((occupant) => ({
            id: occupant.id,
            firstName: occupant.firstName,
            middleName: occupant.middleName,
            lastName: occupant.lastName,
            preferredName: occupant.preferredName,
            relationship: defaultToDefault(occupant.relationshipId),
            affordableRelationship: defaultToDefault(
              occupant.affordableRelationshipId,
            ),
            suffixId: defaultToDefault(occupant.suffixId),
            age: (occupant.age || '').toString(),
            birthday: occupant?.birthday,
          }))
      : [];
  }
  return [];
}

export function generateInitialPet(prospect?: Prospect) {
  const defaultToBlank = defaultTo('');
  const defaultToDefault = defaultTo('default');
  if (prospect) {
    return prospect.petOccupants
      ? prospect.petOccupants.map((occupant) => ({
          id: occupant.id,
          name: occupant.name,
          petTypeId: defaultToDefault(occupant.petTypeId),
          breedId: defaultToDefault(occupant.petBreedId),
          weight: defaultToBlank(occupant.weight),
          petTag: defaultToBlank(occupant.petTag),
          isServiceAnimal: occupant.serviceAnimal,
        }))
      : [];
  }
  return [];
}

/**
 * Extract contactId from adult's phones or emails arrays
 * Tries phones first, then falls back to emails
 * @param {Object} adult - Adult object that may contain phones and emails arrays
 * @returns {string|null} The contactId if found, or null
 */
export function getContactIdFromAdult(adult: Object): string | null {
  // Try to get contactId from phones first
  if (adult?.phones?.length > 0 && adult.phones[0].contactId) {
    return adult.phones[0].contactId;
  }

  // If not found in phones, try emails
  if (adult?.emails?.length > 0 && adult.emails[0].contactId) {
    return adult.emails[0].contactId;
  }

  return null;
}

/**
 * Normalize form data before validation and submission
 * Converts phoneType and emailType to lowercase to ensure consistency
 * @param {Object} values - Form values to normalize
 * @returns {Object} Normalized form values
 */
export function normalizeFormData(values: Object): Object {
  if (!values) return values;

  return {
    ...values,
    adults: values.adults?.map((adult) => ({
      ...adult,
      phones: adult.phones?.map((phone) => ({
        ...phone,
        phoneType: phone.phoneType?.toLowerCase?.() || phone.phoneType,
      })),
      emails: adult.emails?.map((email) => ({
        ...email,
        emailType: email.emailType?.toLowerCase?.() || email.emailType,
      })),
    })),
  };
}

export default {
  getAffordableRelationshipDefault,
  generateInitialAdult,
  generateInitialPet,
  generateInitialMinor,
  getContactIdFromAdult,
  normalizeFormData,
};
